import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React, {useCallback, useState} from 'react';

import EntryActions from 'actions/EntryActions';
import EntryDAO from 'dao/EntryDAO';
import FileStates from 'FileStates';
import {TargetEntry} from 'folder-items/FileConstants';
import {EntrySummary} from 'notebook/ELNConstants';
import EntrySearchInput from 'notebook/entries/EntrySearchInput';
import {prepareNotePartForSave} from 'notebook/notes/NoteStoreTransforms';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import {FilterKey, Operator} from 'search/SearchConstants';
import {Item} from 'ui-library/attach-dropdown/ItemSearchInput';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal, {ModalButton} from 'ui-library/modals/Modal';

export interface AddToEntryResult {
  entryId: string;
  url: string;
}

export interface AddToEntryModalProps {
  getContentsToInsert: (
    entryId: string
  ) => Promise<{noteParts?: Array<NotePart>; sourceDocumentApiId?: string}>;
  onRequestSubmit?: (result: AddToEntryResult) => void | Promise<void>;
  onRequestClose: () => void;
  notePartsContentSourceId?: string;
}

export function AddToEntryModal({
  getContentsToInsert,
  onRequestSubmit,
  onRequestClose,
  notePartsContentSourceId,
}: AddToEntryModalProps): JSX.Element {
  const [selectedEntries, setSelectedEntries] = useState<Array<EntrySummary | TargetEntry | Item>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleEntriesChange = useCallback((entries: Array<EntrySummary | TargetEntry | Item>) => {
    setSelectedEntries(entries);
  }, []);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (selectedEntries.length === 0) {
      return;
    }

    const selectedEntry = selectedEntries[0];
    if (!selectedEntry.id) {
      return;
    }

    setIsSubmitting(true);

    try {
      const {noteParts, sourceDocumentApiId} = await getContentsToInsert(selectedEntry.id);
      assert(
        noteParts || sourceDocumentApiId,
        'Either note parts or a source document API ID needs to be defined'
      );
      const preparedNoteParts = noteParts?.map((notePart) => prepareNotePartForSave(notePart, {}));
      const result = await EntryDAO.addToEntry({
        entryApiId: selectedEntry.id,
        noteParts: preparedNoteParts,
        sourceDocumentApiId,
        notePartsContentSourceId,
      });

      if (result) {
        onRequestSubmit?.({
          entryId: selectedEntry.id,
          url: result.url,
        });
        onRequestClose();
        // Open the entry to ensure we have the latest version
        EntryActions.open(selectedEntry.id);
        // Navigate to the updated entry
        FileStates.get().openFolderItem(selectedEntry.id);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedEntries, onRequestSubmit, onRequestClose, getContentsToInsert, notePartsContentSourceId]);

  const canSubmit = selectedEntries.length > 0 && selectedEntries[0]?.id && !isSubmitting;

  const modalButtons: Array<ModalButton> = [
    {
      text: 'Cancel',
      cancel: true,
      onClick: onRequestClose,
      disabled: isSubmitting,
    },
    {
      text: 'Add to Entry',
      submit: true,
      onClick: handleSubmit,
      disabled: !canSubmit,
      style: ButtonStyle.PRIMARY,
    },
  ];

  return (
    <Modal title="Add to Entry" onRequestClose={onRequestClose} buttons={modalButtons}>
      <div className={css(styles.modalContent)}>
        <p className={css(styles.instructionText)}>Select an entry to add the draft document content to:</p>
        <EntrySearchInput
          selectedEntries={selectedEntries}
          onChange={handleEntriesChange}
          shouldAllowMultiple={false}
          placeholder="Search for an entry..."
          autoFocus={false}
          additionalFilters={[
            {
              key: FilterKey.IS_FILL_IN_ENTRY,
              operator: Operator.IS_FALSE,
              value: null,
            },
          ]}
        />
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContent: {
    padding: '16px 0',
  },

  instructionText: {
    marginBottom: '16px',
  },
});
